import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import type { RequestState } from "@redux/state/shared-types";
import type { ExternalProviderDashboardVM } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { retrieveExternalProviderDashboard } from "@academy-context/read/application/use-cases/external-provider/dashboard-retrieval/retrieve-external-provider-dashboard";
import { selectExternalProviderDashboardRetrieval } from "@academy-context/read/application/use-cases/external-provider/dashboard-retrieval/selectors/external-provider-dashboard-selectors";

interface UseExternalProviderDashboardResult {
  data: Nullable<ExternalProviderDashboardVM>;
  fetching: RequestState;
  error: Nullable<string>;
  refetch: () => void;
}

export const useExternalProviderDashboard = (): UseExternalProviderDashboardResult => {
  const dispatch = useAppDispatch();
  const repository = useExternalProviderRepository();
  const { data, fetching, error } = useAppSelector(selectExternalProviderDashboardRetrieval);

  const fetchDashboard = useCallback(() => {
    dispatch(retrieveExternalProviderDashboard({ externalProviderRepository: repository }));
  }, [dispatch, repository]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, fetching, error, refetch: fetchDashboard };
};
